import { useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  RefreshCw,
  Star,
  ShoppingBag,
  TrendingUp,
  MapPin,
  Calendar,
  ExternalLink,
  Store,
  MessageSquare,
  Users,
  DollarSign,
  BarChart3,
  Search,
  ArrowUpDown,
  ChevronDown,
  ChevronUp,
  Filter,
} from "lucide-react";
import { apiClient } from "../lib/api";
import SalesEstimateSection from "../components/shops/SalesEstimateSection";
import type { ShopListing, ShopReview, ReviewByListing, ShopSnapshot, DailyCount, MonthlyCount } from "@erc/shared";

// ============ LIGHTBOX ============

function ImageLightbox({
  src,
  title,
  url,
  onClose,
  onAddToBuilder,
}: {
  src: string;
  title?: string;
  url?: string | null;
  onClose: () => void;
  onAddToBuilder?: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="relative flex flex-col items-center gap-4 rounded-2xl bg-white p-4 shadow-2xl max-w-[90vw]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute -top-3 -right-3 flex h-8 w-8 items-center justify-center rounded-full bg-white text-gray-500 shadow-md hover:bg-gray-100 text-lg font-bold"
        >
          &times;
        </button>
        <img
          src={src.replace(/il_\d+x\d+/, 'il_1588xN')}
          alt=""
          className="max-h-[80vh] w-auto rounded-xl object-contain"
          style={{ minHeight: "60vh" }}
        />
        {title && (
          <p className="text-sm font-medium text-gray-700 text-center line-clamp-2 max-w-md">{title}</p>
        )}
        <div className="flex gap-2">
          <button
            className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 transition-colors"
            onClick={onAddToBuilder}
          >
            <ShoppingBag className="h-4 w-4" />
            Listeye Ekle
          </button>
          {url && (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <ExternalLink className="h-4 w-4" />
              Etsy'de Gör
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ SUB COMPONENTS ============

function StatCard({
  label,
  value,
  subtitle,
  icon: Icon,
  color,
}: {
  label: string;
  value: string | number;
  subtitle?: string;
  icon: React.ElementType;
  color: string;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-gray-400">{label}</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">{value}</p>
          {subtitle && <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>}
        </div>
        <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${color}`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
      </div>
    </div>
  );
}

function ListingRow({
  listing,
  rank,
  onImageClick,
}: {
  listing: ShopListing;
  rank?: number;
  onImageClick?: (info: { src: string; title?: string; url?: string | null }) => void;
}) {
  return (
    <a
      href={listing.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex items-center gap-3 rounded-lg border border-gray-100 bg-white px-3 py-2.5 transition-all hover:border-indigo-200 hover:shadow-sm"
    >
      {rank != null && (
        <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-bold text-gray-500">
          {rank}
        </span>
      )}
      {listing.imageUrl ? (
        <img
          src={listing.imageUrl}
          alt=""
          className="h-12 w-12 rounded-lg object-cover flex-shrink-0 hover:opacity-80"
          onClick={(e) => {
            if (onImageClick) { e.preventDefault(); e.stopPropagation(); onImageClick({ src: listing.imageUrl!, title: listing.title, url: listing.url }); }
          }}
        />
      ) : (
        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-gray-50">
          <ShoppingBag className="h-5 w-5 text-gray-300" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900 line-clamp-1 group-hover:text-indigo-600">
          {listing.title}
        </p>
        <div className="mt-0.5 flex items-center gap-3 text-xs text-gray-400">
          {listing.price && (
            <span className="font-semibold text-gray-700">
              {listing.currency === "TRY" ? "\u20BA" : "$"}
              {listing.price}
            </span>
          )}
          {listing.firstSeenAt && (
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {new Date(listing.firstSeenAt).toLocaleDateString("tr-TR", { day: "numeric", month: "short", year: "numeric" })}
            </span>
          )}
        </div>
      </div>
    </a>
  );
}

function RatingBar({
  stars,
  count,
  maxCount,
}: {
  stars: number;
  count: number;
  maxCount: number;
}) {
  const pct = maxCount > 0 ? (count / maxCount) * 100 : 0;
  return (
    <div className="flex items-center gap-2">
      <span className="w-4 text-right text-xs font-medium text-gray-600">{stars}</span>
      <Star className="h-3 w-3 text-yellow-500 fill-yellow-500 flex-shrink-0" />
      <div className="flex-1">
        <div className="h-2.5 overflow-hidden rounded-full bg-gray-100">
          <div
            className="h-full rounded-full bg-yellow-400 transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
      <span className="w-8 text-right text-xs text-gray-500">{count}</span>
    </div>
  );
}

function CategoryBar({ data }: { data: { category: string; count: number }[] }) {
  const total = data.reduce((sum, d) => sum + d.count, 0);
  const colors = [
    "bg-indigo-500",
    "bg-purple-500",
    "bg-emerald-500",
    "bg-orange-500",
    "bg-pink-500",
    "bg-cyan-500",
    "bg-yellow-500",
    "bg-red-500",
  ];

  return (
    <div className="space-y-2">
      {data.slice(0, 8).map((item, i) => (
        <div key={item.category} className="flex items-center gap-3">
          <div className="w-28 truncate text-xs text-gray-600">{item.category}</div>
          <div className="flex-1">
            <div className="h-5 overflow-hidden rounded-full bg-gray-100">
              <div
                className={`h-full rounded-full ${colors[i % colors.length]} transition-all`}
                style={{ width: `${(item.count / total) * 100}%` }}
              />
            </div>
          </div>
          <span className="w-8 text-right text-xs font-medium text-gray-700">{item.count}</span>
        </div>
      ))}
    </div>
  );
}

function ReviewCard({ review }: { review: ShopReview }) {
  return (
    <div className="rounded-lg border border-gray-100 p-4 transition-colors hover:bg-gray-50/50">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {review.rating && (
            <div className="flex items-center gap-0.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star
                  key={i}
                  className={`h-3.5 w-3.5 ${
                    i < review.rating!
                      ? "text-yellow-500 fill-yellow-500"
                      : "text-gray-200 fill-gray-200"
                  }`}
                />
              ))}
            </div>
          )}
          {review.reviewerName && (
            <span className="text-sm font-medium text-gray-800">
              {review.reviewerName}
            </span>
          )}
        </div>
        {review.reviewDate && (
          <span className="text-xs text-gray-400">{review.reviewDate}</span>
        )}
      </div>
      {review.text && (
        <p className="text-sm text-gray-600 leading-relaxed">{review.text}</p>
      )}
      {review.listingTitle && (
        <div className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-gray-50 px-2 py-1">
          <ShoppingBag className="h-3 w-3 text-gray-400" />
          {review.listingUrl ? (
            <a
              href={review.listingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-indigo-600 hover:text-indigo-700 line-clamp-1"
            >
              {review.listingTitle}
            </a>
          ) : (
            <span className="text-xs text-gray-500 line-clamp-1">
              {review.listingTitle}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function MonthlyReviewChart({ data }: { data: MonthlyCount[] }) {
  if (data.length === 0) return null;
  const maxCount = Math.max(...data.map((d) => d.count));
  const MAX_BAR_H = 100; // px

  return (
    <div className="flex items-end gap-1 overflow-x-auto pb-1">
      {data.map((d) => {
        const h = maxCount > 0 ? Math.max((d.count / maxCount) * MAX_BAR_H, 4) : 4;
        return (
          <div key={d.key} className="flex flex-col items-center gap-1 min-w-[28px] flex-shrink-0">
            <span className="text-[10px] font-semibold text-gray-600 tabular-nums">{d.count}</span>
            <div className="w-5 rounded-t-sm bg-indigo-400" style={{ height: `${h}px` }} />
            <span className="text-[9px] text-gray-400 whitespace-nowrap">{d.label}</span>
          </div>
        );
      })}
    </div>
  );
}

function DailyReviewChart({ data }: { data: DailyCount[] }) {
  if (data.length === 0) return null;
  const maxCount = Math.max(...data.map((d) => d.count));
  const MAX_BAR_H = 100;

  return (
    <div className="flex items-end gap-1 overflow-x-auto pb-1">
      {data.map((d) => {
        const h = maxCount > 0 ? Math.max((d.count / maxCount) * MAX_BAR_H, 4) : 4;
        return (
          <div key={d.key} className="flex flex-col items-center gap-1 min-w-[28px] flex-shrink-0">
            <span className="text-[10px] font-semibold text-gray-600 tabular-nums">{d.count}</span>
            <div className="w-5 rounded-t-sm bg-emerald-400" style={{ height: `${h}px` }} />
            <span className="text-[9px] text-gray-400 whitespace-nowrap">{d.label}</span>
            <span className="text-[8px] text-gray-300">{d.dayName}</span>
          </div>
        );
      })}
    </div>
  );
}

// ============ MAIN PAGE ============

type ListingSortKey = "title" | "price" | "firstSeenAt";
type ReviewDateFilter = "all" | "this_week" | "this_month" | "last_month" | "last_3_months" | "last_6_months" | "custom";

function getMonthKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function getFilterMonthKeys(filter: ReviewDateFilter, customStart?: string, customEnd?: string): Set<string> | null {
  if (filter === "all") return null; // no filter
  const now = new Date();
  const keys = new Set<string>();

  if (filter === "custom" && customStart && customEnd) {
    // customStart/customEnd are "YYYY-MM" format
    const [sy, sm] = customStart.split("-").map(Number);
    const [ey, em] = customEnd.split("-").map(Number);
    let y = sy, m = sm;
    while (y < ey || (y === ey && m <= em)) {
      keys.add(`${y}-${String(m).padStart(2, "0")}`);
      m++;
      if (m > 12) { m = 1; y++; }
    }
    return keys;
  }

  if (filter === "this_week" || filter === "this_month") {
    keys.add(getMonthKey(now));
    return keys;
  }

  if (filter === "last_month") {
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    keys.add(getMonthKey(prev));
    return keys;
  }

  // Son N ay
  const months = filter === "last_3_months" ? 3 : 6;
  for (let i = 0; i < months; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    keys.add(getMonthKey(d));
  }
  return keys;
}

export default function ShopDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Listing tab state
  const [listingSearch, setListingSearch] = useState("");
  const [listingSort, setListingSort] = useState<ListingSortKey>("firstSeenAt");
  const [listingSortAsc, setListingSortAsc] = useState(false);
  const [showAllListings, setShowAllListings] = useState(false);

  // Review tab state
  const [showAllReviews, setShowAllReviews] = useState(false);

  // Review date filter state
  const [reviewDateFilter, setReviewDateFilter] = useState<ReviewDateFilter>("all");
  const [customStartMonth, setCustomStartMonth] = useState("");
  const [customEndMonth, setCustomEndMonth] = useState("");

  // Lightbox state
  const [lightbox, setLightbox] = useState<{ src: string; title?: string; url?: string | null } | null>(null);

  const { data: shop, isLoading: shopLoading } = useQuery({
    queryKey: ["shop", id],
    queryFn: () => apiClient.getShop(id!),
    enabled: !!id,
  });

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ["shop-stats", id],
    queryFn: () => apiClient.getShopStats(id!),
    enabled: !!id,
  });

  const refreshMutation = useMutation({
    mutationFn: () => apiClient.refreshShop(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shop", id] });
      queryClient.invalidateQueries({ queryKey: ["shop-stats", id] });
    },
  });

  // All useMemo hooks MUST be before early returns (React rules of hooks)
  const filteredReviewsByListing = useMemo(() => {
    const items = stats?.reviewsByListing ?? [];
    const monthKeys = getFilterMonthKeys(reviewDateFilter, customStartMonth, customEndMonth);
    if (!monthKeys) return items; // "all" - no filter

    // Daily filtre icin tarih araligi hesapla
    const now = new Date();
    let dayStart = "";
    let dayEnd = "9999-12-31";
    if (reviewDateFilter === "this_week") {
      const weekAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
      dayStart = `${weekAgo.getFullYear()}-${String(weekAgo.getMonth() + 1).padStart(2, "0")}-${String(weekAgo.getDate()).padStart(2, "0")}`;
    } else if (reviewDateFilter === "this_month") {
      dayStart = `${getMonthKey(now)}-01`;
      dayEnd = `${getMonthKey(now)}-31`;
    } else if (reviewDateFilter === "last_month") {
      const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      dayStart = `${getMonthKey(prev)}-01`;
      dayEnd = `${getMonthKey(prev)}-31`;
    } else if (reviewDateFilter === "custom" && customStartMonth && customEndMonth) {
      dayStart = `${customStartMonth}-01`;
      dayEnd = `${customEndMonth}-31`;
    }

    return items
      .map((item: ReviewByListing) => {
        const filteredMonthly = (item.monthly ?? []).filter((m) => monthKeys.has(m.key));
        const filteredDaily = (item.daily ?? []).filter((d) => d.key >= dayStart && d.key <= dayEnd);
        const filteredCount = filteredMonthly.reduce((s, m) => s + m.count, 0);
        return { ...item, count: filteredCount, monthly: filteredMonthly, daily: filteredDaily };
      })
      .filter((item) => item.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [stats?.reviewsByListing, reviewDateFilter, customStartMonth, customEndMonth]);

  const filteredDailyData = useMemo(() => {
    const daily = stats?.dailyDistribution ?? [];
    if (reviewDateFilter === "all") return [];

    const now = new Date();
    if (reviewDateFilter === "this_week") {
      // Son 7 gün
      const weekAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
      const startKey = `${weekAgo.getFullYear()}-${String(weekAgo.getMonth() + 1).padStart(2, "0")}-${String(weekAgo.getDate()).padStart(2, "0")}`;
      return daily.filter((d) => d.key >= startKey);
    }

    if (reviewDateFilter === "this_month") {
      const prefix = getMonthKey(now);
      return daily.filter((d) => d.key.startsWith(prefix));
    }

    if (reviewDateFilter === "last_month") {
      const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const prefix = getMonthKey(prev);
      return daily.filter((d) => d.key.startsWith(prefix));
    }

    if (reviewDateFilter === "custom" && customStartMonth && customEndMonth) {
      return daily.filter((d) => {
        const monthPart = d.key.substring(0, 7);
        return monthPart >= customStartMonth && monthPart <= customEndMonth;
      });
    }

    return [];
  }, [stats?.dailyDistribution, reviewDateFilter, customStartMonth, customEndMonth]);

  // Filtered monthly data for chart
  const filteredMonthlyData = useMemo(() => {
    const monthly = stats?.monthlyDistribution ?? [];
    if (reviewDateFilter === "all") return monthly;
    const monthKeys = getFilterMonthKeys(reviewDateFilter, customStartMonth, customEndMonth);
    if (!monthKeys) return monthly;
    return monthly.filter((m) => monthKeys.has(m.key));
  }, [stats?.monthlyDistribution, reviewDateFilter, customStartMonth, customEndMonth]);

  // Early returns (after all hooks)
  if (shopLoading || statsLoading) {
    return (
      <div className="mx-auto max-w-6xl p-8">
        <div className="h-8 w-48 animate-pulse rounded bg-gray-200 mb-6" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-24 animate-pulse rounded-xl bg-gray-100" />
          ))}
        </div>
        <div className="grid grid-cols-2 gap-6">
          <div className="h-64 animate-pulse rounded-xl bg-gray-100" />
          <div className="h-64 animate-pulse rounded-xl bg-gray-100" />
        </div>
      </div>
    );
  }

  if (!shop) {
    return (
      <div className="flex flex-col items-center justify-center p-20 text-center">
        <Store className="mb-3 h-12 w-12 text-gray-300" />
        <p className="text-gray-500">Mağaza bulunamadı</p>
        <Link to="/shops" className="mt-3 text-sm text-indigo-600 hover:text-indigo-700">
          Geri dön
        </Link>
      </div>
    );
  }

  // Derived data
  const filteredListings = (stats?.allListings ?? [])
    .filter((l) =>
      listingSearch
        ? l.title.toLowerCase().includes(listingSearch.toLowerCase())
        : true
    )
    .sort((a, b) => {
      let cmp = 0;
      if (listingSort === "title") cmp = a.title.localeCompare(b.title);
      else if (listingSort === "price")
        cmp = (parseFloat(a.price ?? "0") || 0) - (parseFloat(b.price ?? "0") || 0);
      else cmp = new Date(b.firstSeenAt).getTime() - new Date(a.firstSeenAt).getTime();
      return listingSortAsc ? cmp : -cmp;
    });

  const displayedListings = showAllListings
    ? filteredListings
    : filteredListings.slice(0, 20);

  const displayedReviews = showAllReviews
    ? (stats?.reviews ?? [])
    : (stats?.reviews ?? []).slice(0, 10);

  const maxRatingCount = Math.max(
    ...(stats?.ratingDistribution?.map((r) => r.count) ?? [0])
  );

  const filteredReviewTotal = filteredReviewsByListing.reduce((s, item) => s + item.count, 0);
  const showDailyChart = reviewDateFilter === "this_week" || reviewDateFilter === "this_month" || reviewDateFilter === "last_month";

  return (
    <div className="mx-auto max-w-6xl p-8">
      {lightbox && (
        <ImageLightbox
          src={lightbox.src}
          title={lightbox.title}
          url={lightbox.url}
          onClose={() => setLightbox(null)}
          onAddToBuilder={async () => {
            try {
              const project = await apiClient.createBuilderProject({
                title: lightbox.title ?? "Isimsiz",
                sourceImageUrl: lightbox.src,
                sourceListingUrl: lightbox.url ?? undefined,
              });
              setLightbox(null);
              navigate(`/listing-builder?id=${project.id}`);
            } catch (err) {
              alert("Proje oluşturulamadı: " + String(err));
            }
          }}
        />
      )}

      {/* Breadcrumb */}
      <Link
        to="/shops"
        className="mb-4 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
      >
        <ArrowLeft className="h-4 w-4" />
        Mağazalar
      </Link>

      {/* Shop Header */}
      <div className="mb-8 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-5">
            {shop.avatarUrl ? (
              <img
                src={shop.avatarUrl}
                alt={shop.name}
                className="h-20 w-20 rounded-2xl object-cover shadow-sm"
              />
            ) : (
              <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-orange-100 to-orange-200">
                <Store className="h-10 w-10 text-orange-600" />
              </div>
            )}
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{shop.name}</h1>
              <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-gray-500">
                {shop.location && (
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5" />
                    {shop.location}
                  </span>
                )}
                {shop.openedDate && (
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    {shop.openedDate}
                  </span>
                )}
                <a
                  href={shop.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-indigo-600 hover:text-indigo-700 font-medium"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  Etsy'de Görüntüle
                </a>
              </div>
              {shop.description && (
                <p className="mt-3 max-w-2xl text-sm text-gray-600 leading-relaxed line-clamp-2">
                  {shop.description}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={() => refreshMutation.mutate()}
            disabled={refreshMutation.isPending}
            className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`h-4 w-4 ${refreshMutation.isPending ? "animate-spin" : ""}`} />
            {refreshMutation.isPending ? "Güncelleniyor..." : "Yenile"}
          </button>
        </div>
      </div>

      {stats && (
        <>
          {/* Stat Cards */}
          <div className="mb-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <StatCard
              label="Toplam Satış"
              value={stats.totalSales?.toLocaleString() ?? "--"}
              icon={ShoppingBag}
              color="bg-indigo-500"
            />
            <StatCard
              label="Toplam Yorum"
              value={stats.totalReviews?.toLocaleString() ?? "--"}
              subtitle={stats.reviewCount > 0 ? `${stats.reviewCount} tarandı` : undefined}
              icon={MessageSquare}
              color="bg-yellow-500"
            />
            <StatCard
              label="Puan"
              value={stats.rating?.toFixed(1) ?? "--"}
              icon={Star}
              color="bg-emerald-500"
            />
            <StatCard
              label="Toplam Ürün"
              value={stats.totalListingCount?.toLocaleString() ?? stats.trackedListingCount}
              subtitle={stats.totalListingCount ? `${stats.trackedListingCount} takip ediliyor` : undefined}
              icon={BarChart3}
              color="bg-purple-500"
            />
            <StatCard
              label="Ort. Fiyat"
              value={stats.avgPrice ? `$${stats.avgPrice.toFixed(2)}` : "--"}
              icon={DollarSign}
              color="bg-pink-500"
            />
            <StatCard
              label="Hayranlar"
              value={stats.admirerCount?.toLocaleString() ?? "--"}
              icon={Users}
              color="bg-cyan-500"
            />
          </div>

          <SalesEstimateSection shopId={shop.id} />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            {/* Price Range */}
            {stats.priceRange && (
              <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                <h3 className="mb-4 text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-gray-400" />
                  Fiyat Aralığı
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Min</span>
                    <span className="text-lg font-bold text-emerald-600">
                      ${stats.priceRange.min.toFixed(2)}
                    </span>
                  </div>
                  <div className="h-px bg-gray-100" />
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Ortalama</span>
                    <span className="text-lg font-bold text-gray-900">
                      ${stats.priceRange.avg.toFixed(2)}
                    </span>
                  </div>
                  <div className="h-px bg-gray-100" />
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Max</span>
                    <span className="text-lg font-bold text-red-600">
                      ${stats.priceRange.max.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Rating Distribution */}
            {stats.ratingDistribution && (
              <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                <h3 className="mb-4 text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <Star className="h-4 w-4 text-gray-400" />
                  Puan Dağılımı
                </h3>
                <div className="space-y-2">
                  {stats.ratingDistribution.map((item) => (
                    <RatingBar
                      key={item.stars}
                      stars={item.stars}
                      count={item.count}
                      maxCount={maxRatingCount}
                    />
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t border-gray-100 text-center">
                  <span className="text-xs text-gray-400">
                    Toplam {stats.ratingDistribution.reduce((s, r) => s + r.count, 0)} puan
                  </span>
                </div>
              </div>
            )}

            {/* Category Breakdown */}
            {stats.categoryBreakdown.length > 0 && stats.categoryBreakdown[0].category !== "Kategorisiz" ? (
              <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                <h3 className="mb-4 text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-gray-400" />
                  Kategori Dağılımı
                </h3>
                <CategoryBar data={stats.categoryBreakdown} />
              </div>
            ) : (
              // Snapshot history if no categories
              stats.snapshots && stats.snapshots.length > 1 && (
                <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                  <h3 className="mb-4 text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-gray-400" />
                    Satış Geçmişi
                  </h3>
                  <SnapshotHistory snapshots={stats.snapshots} />
                </div>
              )
            )}
          </div>

          {/* Review Distribution Chart */}
          {stats.monthlyDistribution && stats.monthlyDistribution.length > 0 && (
            <div className="mb-8 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <h3 className="mb-4 text-sm font-semibold text-gray-900 flex items-center gap-2">
                <Calendar className="h-4 w-4 text-gray-400" />
                {showDailyChart ? "Günlük Yorum Dağılımı" : "Aylık Yorum Dağılımı"}
                <span className="ml-auto rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-medium text-indigo-600">
                  {(showDailyChart ? filteredDailyData : filteredMonthlyData).reduce((s, d) => s + d.count, 0)} yorum
                </span>
              </h3>
              {showDailyChart ? (
                filteredDailyData.length > 0 ? (
                  <DailyReviewChart data={filteredDailyData} />
                ) : (
                  <p className="py-4 text-center text-sm text-gray-400">Bu dönemde yorum yok</p>
                )
              ) : (
                <MonthlyReviewChart data={reviewDateFilter === "all" ? stats.monthlyDistribution : filteredMonthlyData} />
              )}
            </div>
          )}

          {/* Review Analysis by Listing */}
          {stats.reviewsByListing && stats.reviewsByListing.length > 0 && (
            <div className="mb-8 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-gray-400" />
                  Ürün Bazlı Yorum Analizi
                  <span className="rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-medium text-indigo-600">
                    {reviewDateFilter === "all" ? stats.reviewCount : filteredReviewTotal} yorum
                  </span>
                </h3>
              </div>

              {/* Date Filters */}
              <div className="flex flex-wrap items-center gap-1.5 mb-4">
                <Filter className="h-3.5 w-3.5 text-gray-400 mr-1" />
                {([
                  ["all", "Tümü"],
                  ["this_week", "Bu Hafta"],
                  ["this_month", "Bu Ay"],
                  ["last_month", "Geçen Ay"],
                  ["last_3_months", "Son 3 Ay"],
                  ["last_6_months", "Son 6 Ay"],
                  ["custom", "Özel"],
                ] as [ReviewDateFilter, string][]).map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => setReviewDateFilter(key)}
                    className={`rounded-lg px-2.5 py-1 text-xs font-medium transition-colors ${
                      reviewDateFilter === key
                        ? "bg-indigo-100 text-indigo-700 border border-indigo-200"
                        : "bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* Custom date range pickers */}
              {reviewDateFilter === "custom" && (
                <div className="flex items-center gap-2 mb-4 p-3 rounded-lg bg-gray-50 border border-gray-200">
                  <span className="text-xs text-gray-500">Başlangıç:</span>
                  <input
                    type="month"
                    value={customStartMonth}
                    onChange={(e) => setCustomStartMonth(e.target.value)}
                    className="h-7 rounded border border-gray-300 px-2 text-xs text-gray-700 focus:border-indigo-300 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                  />
                  <span className="text-xs text-gray-500">Bitiş:</span>
                  <input
                    type="month"
                    value={customEndMonth}
                    onChange={(e) => setCustomEndMonth(e.target.value)}
                    className="h-7 rounded border border-gray-300 px-2 text-xs text-gray-700 focus:border-indigo-300 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                  />
                </div>
              )}

              {filteredReviewsByListing.length === 0 ? (
                <p className="py-6 text-center text-sm text-gray-400">Bu dönemde yorum bulunamadı</p>
              ) : (
                <div className="space-y-2">
                  {filteredReviewsByListing.slice(0, 15).map((item: ReviewByListing, i: number) => (
                    <div
                      key={i}
                      className="rounded-lg border border-gray-100 px-3 py-2.5 hover:bg-gray-50/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-indigo-50 text-xs font-bold text-indigo-600">
                          {i + 1}
                        </span>
                        {item.imageUrl ? (
                          <img
                            src={item.imageUrl}
                            alt=""
                            className="h-10 w-10 rounded-lg object-cover flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => setLightbox({ src: item.imageUrl!, title: item.title, url: item.url })}
                          />
                        ) : (
                          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-gray-50">
                            <ShoppingBag className="h-4 w-4 text-gray-300" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          {item.url ? (
                            <a
                              href={item.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-medium text-gray-900 hover:text-indigo-600 line-clamp-1"
                            >
                              {item.title}
                            </a>
                          ) : (
                            <p className="text-sm font-medium text-gray-900 line-clamp-1">
                              {item.title}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          {item.avgRating > 0 && (
                            <span className="flex items-center gap-1 text-xs text-gray-500">
                              <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                              {item.avgRating.toFixed(1)}
                            </span>
                          )}
                          <span className="rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-semibold text-indigo-700 tabular-nums">
                            {item.count}
                          </span>
                        </div>
                      </div>
                      {(() => {
                        const chartData = showDailyChart && item.daily && item.daily.length > 0
                          ? item.daily
                          : item.monthly && item.monthly.length > 1
                            ? item.monthly
                            : null;
                        if (!chartData) return null;
                        const isDaily = showDailyChart && item.daily && item.daily.length > 0;
                        const maxC = Math.max(...chartData.map((m) => m.count));
                        return (
                          <div className="mt-2 ml-9 flex items-end gap-1">
                            {chartData.map((m) => {
                              const h = maxC > 0 ? Math.max((m.count / maxC) * 28, 4) : 4;
                              return (
                                <div key={m.key} className="flex flex-col items-center gap-0.5 min-w-[20px]">
                                  <span className="text-[9px] font-medium text-gray-500 tabular-nums">{m.count}</span>
                                  <div
                                    className={`w-3 rounded-t-sm ${isDaily ? "bg-emerald-300" : "bg-indigo-300"}`}
                                    style={{ height: `${h}px` }}
                                  />
                                  <span className="text-[8px] text-gray-400 whitespace-nowrap">{m.label.split(" ")[0]}</span>
                                  {isDaily && "dayName" in m && (
                                    <span className="text-[7px] text-gray-300">{(m as any).dayName}</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* All Listings */}
          <div className="mb-8 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <ShoppingBag className="h-4 w-4 text-gray-400" />
                Ürünler
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
                  {filteredListings.length}
                </span>
              </h3>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Ürün ara..."
                    value={listingSearch}
                    onChange={(e) => setListingSearch(e.target.value)}
                    className="h-8 rounded-lg border border-gray-200 bg-gray-50 pl-8 pr-3 text-xs text-gray-700 focus:border-indigo-300 focus:outline-none focus:ring-1 focus:ring-indigo-300 w-48"
                  />
                </div>
                <button
                  onClick={() => {
                    if (listingSort === "price") {
                      setListingSortAsc(!listingSortAsc);
                    } else {
                      setListingSort("price");
                      setListingSortAsc(true);
                    }
                  }}
                  className={`flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                    listingSort === "price"
                      ? "border-indigo-200 bg-indigo-50 text-indigo-700"
                      : "border-gray-200 text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  <ArrowUpDown className="h-3 w-3" />
                  Fiyat
                </button>
                <button
                  onClick={() => {
                    if (listingSort === "firstSeenAt") {
                      setListingSortAsc(!listingSortAsc);
                    } else {
                      setListingSort("firstSeenAt");
                      setListingSortAsc(false);
                    }
                  }}
                  className={`flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                    listingSort === "firstSeenAt"
                      ? "border-indigo-200 bg-indigo-50 text-indigo-700"
                      : "border-gray-200 text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  <ArrowUpDown className="h-3 w-3" />
                  Tarih
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {displayedListings.map((listing, i) => (
                <ListingRow
                  key={listing.id}
                  listing={listing}
                  rank={listingSort === "price" ? undefined : i + 1}
                  onImageClick={setLightbox}
                />
              ))}
            </div>
            {filteredListings.length === 0 && (
              <p className="py-8 text-center text-sm text-gray-400">Ürün bulunamadı</p>
            )}
            {filteredListings.length > 20 && (
              <button
                onClick={() => setShowAllListings(!showAllListings)}
                className="mt-3 flex w-full items-center justify-center gap-1 rounded-lg border border-gray-200 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors"
              >
                {showAllListings ? (
                  <>
                    <ChevronUp className="h-3.5 w-3.5" />
                    Daha az göster
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-3.5 w-3.5" />
                    Tümü ({filteredListings.length})
                  </>
                )}
              </button>
            )}
          </div>

          {/* Reviews */}
          {stats.reviews && stats.reviews.length > 0 && (
            <div className="mb-8 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <h3 className="mb-4 text-sm font-semibold text-gray-900 flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-gray-400" />
                Son Yorumlar
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
                  {stats.reviews.length}
                </span>
              </h3>
              <div className="space-y-2">
                {displayedReviews.map((review: ShopReview) => (
                  <ReviewCard key={review.id} review={review} />
                ))}
              </div>
              {stats.reviews.length > 10 && (
                <button
                  onClick={() => setShowAllReviews(!showAllReviews)}
                  className="mt-3 flex w-full items-center justify-center gap-1 rounded-lg border border-gray-200 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  {showAllReviews ? (
                    <>
                      <ChevronUp className="h-3.5 w-3.5" />
                      Daha az göster
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-3.5 w-3.5" />
                      Tüm yorumlar ({stats.reviews.length})
                    </>
                  )}
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Snapshot history - simple text-based trend
function SnapshotHistory({ snapshots }: { snapshots: ShopSnapshot[] }) {
  // Show last few snapshots as a simple list
  const sorted = [...snapshots].reverse().slice(-5);
  return (
    <div className="space-y-2">
      {sorted.map((s) => (
        <div key={s.id} className="flex items-center justify-between text-xs">
          <span className="text-gray-400">
            {new Date(s.capturedAt).toLocaleDateString("tr-TR")}
          </span>
          <div className="flex items-center gap-4">
            {s.totalSales != null && (
              <span className="text-gray-600">
                <span className="font-medium">{s.totalSales.toLocaleString()}</span> satış
              </span>
            )}
            {s.totalReviews != null && (
              <span className="text-gray-600">
                <span className="font-medium">{s.totalReviews.toLocaleString()}</span> yorum
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
